import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BarChart3, Map, Loader2, RefreshCw, ArrowRight, TrendingUp,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  Legend,
} from "recharts";
import { api, formatApiError } from "@/lib/api";
import "./CommunityIntelligence.css";

const DIMENSION_LABELS = {
  organization: "Organization",
  community: "Community",
  county: "County",
  region: "Region",
  state: "State",
  program: "Program",
  service_type: "Service Type",
};

const WINDOWS = [
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
  { label: "365d", days: 365 },
];

export default function CommunityIntelligence() {
  const [dimension, setDimension] = useState("county");
  const [days, setDays] = useState(90);
  const [value, setValue] = useState("");
  const [availableValues, setAvailableValues] = useState({});
  const [kpis, setKpis] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadValues = useCallback(async () => {
    try {
      const { data } = await api.get("/analytics/dimensions");
      setAvailableValues(data.values || {});
    } catch (e) { console.error(formatApiError(e)); }
  }, []);
  useEffect(() => { loadValues(); }, [loadValues]);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const query = new URLSearchParams({ dimension, window_days: String(days) });
      if (value) query.set("value", value);
      const [k, t] = await Promise.all([
        api.get(`/analytics/kpis?${query.toString()}`),
        api.get(`/analytics/trends?${query.toString()}`),
      ]);
      setKpis(k.data);
      setTrends(t.data);
    } catch (e) {
      setError(formatApiError(e));
    } finally { setLoading(false); }
  }, [dimension, days, value]);
  useEffect(() => { load(); }, [load]);

  const dimensionValues = availableValues[dimension] || [];

  return (
    <div className="hw-ci" data-testid="community-intelligence">
      <div className="hw-ci__eyebrow">
        <BarChart3 className="w-3.5 h-3.5"
                    style={{ display: "inline", verticalAlign: "text-bottom",
                              marginRight: "0.3rem" }} />
        Community Intelligence
      </div>
      <h1 className="hw-ci__title">Impact analytics across every level</h1>
      <p className="hw-ci__sub">
        Slice referrals, resources, care outcomes, volunteers, and wait times by
        Organization, Community, County, Region, State, Program, or Service Type.
      </p>

      <div className="hw-ci__controls">
        <select className="hw-ci__select" value={dimension}
                 onChange={(e) => { setDimension(e.target.value); setValue(""); }}
                 data-testid="ci-dimension">
          {Object.entries(DIMENSION_LABELS).map(([k, l]) => (
            <option key={k} value={k}>{l}</option>
          ))}
        </select>

        <select className="hw-ci__select" value={value}
                 onChange={(e) => setValue(e.target.value)}
                 data-testid="ci-value">
          <option value="">All {DIMENSION_LABELS[dimension]}s</option>
          {dimensionValues.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>

        {WINDOWS.map((w) => (
          <button key={w.days}
                   className={`hw-ci__btn ${days === w.days ? "hw-ci__btn--primary" : ""}`}
                   onClick={() => setDays(w.days)}
                   data-testid={`ci-window-${w.label}`}>
            {w.label}
          </button>
        ))}

        <button className="hw-ci__btn" onClick={load}
                 data-testid="ci-refresh"
                 style={{ marginLeft: "auto" }}>
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>

        <Link to="/analytics/resource-desert" className="hw-ci__btn hw-ci__btn--primary"
               data-testid="ci-open-desert">
          <Map className="w-3.5 h-3.5" /> Resource Desert Detector
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>

        <Link to="/analytics/funder-dashboard" className="hw-ci__btn"
               data-testid="ci-open-funder">
          <TrendingUp className="w-3.5 h-3.5" /> Funder Dashboard
        </Link>
      </div>

      {error && <div style={{ color: "#B91C1C" }}>{error}</div>}
      {loading && !kpis && (
        <div style={{ color: "#0F766E" }}>
          <Loader2 className="w-4 h-4 animate-spin"
                    style={{ display: "inline", verticalAlign: "text-bottom" }} /> loading…
        </div>
      )}

      {kpis && (
        <>
          <div className="hw-ci__kpi-grid">
            <Kpi label="Care seekers" value={kpis.kpis.care_seekers} testid="ci-kpi-cs" />
            <Kpi label="Referrals" value={kpis.kpis.referrals_total} testid="ci-kpi-referrals" />
            <Kpi label="Completed" value={kpis.kpis.referrals_completed} testid="ci-kpi-completed" />
            <Kpi label="Completion rate" value={`${kpis.kpis.completion_rate_pct}%`} testid="ci-kpi-rate" />
            <Kpi label="Active resources" value={kpis.kpis.resources_active} testid="ci-kpi-resources" />
            <Kpi label="Active volunteers" value={kpis.kpis.volunteers_active} testid="ci-kpi-volunteers" />
            <Kpi label="Appointments" value={kpis.kpis.appointments_total} testid="ci-kpi-appts" />
            <Kpi label="Avg wait (days)"
                  value={kpis.kpis.avg_wait_days != null ? kpis.kpis.avg_wait_days : "—"}
                  testid="ci-kpi-wait" />
          </div>

          {trends && (
            <div className="hw-ci__panel" data-testid="ci-trends-chart">
              <h2>
                <span>Trend series — last {days}d</span>
                <small>Referrals · Care seekers created · Completed · Appointments</small>
              </h2>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={trends.series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" stroke="#64748B" tick={{ fontSize: 10 }}
                          tickFormatter={(d) => d.slice(5)} />
                  <YAxis stroke="#64748B" tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="referrals" stroke="#0F766E"
                         strokeWidth={2} dot={false} name="Referrals" />
                  <Line type="monotone" dataKey="completed" stroke="#22C55E"
                         strokeWidth={2} dot={false} name="Completed" />
                  <Line type="monotone" dataKey="care_seekers" stroke="#3B82F6"
                         strokeWidth={2} dot={false} name="New care seekers" />
                  <Line type="monotone" dataKey="appointments" stroke="#F59E0B"
                         strokeWidth={2} dot={false} name="Appointments" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="hw-ci__panel" data-testid="ci-buckets-table">
            <h2>
              <span>{DIMENSION_LABELS[dimension]} breakdown</span>
              <small>{kpis.buckets.length} keys</small>
            </h2>
            <table className="hw-ci__table">
              <thead>
                <tr>
                  <th>{DIMENSION_LABELS[dimension]}</th>
                  <th>Referrals</th>
                  <th>Completed</th>
                  <th>Declined</th>
                  <th>Care seekers</th>
                  <th>Resources</th>
                  <th>Volunteers</th>
                  <th>Appts</th>
                </tr>
              </thead>
              <tbody>
                {kpis.buckets.map((b) => (
                  <tr key={b.key} data-testid={`ci-bucket-${b.key}`}>
                    <td><strong>{b.key}</strong></td>
                    <td>{b.referrals}</td>
                    <td>{b.completed}</td>
                    <td>{b.declined}</td>
                    <td>{b.care_seekers}</td>
                    <td>{b.resources}</td>
                    <td>{b.volunteers}</td>
                    <td>{b.appointments}</td>
                  </tr>
                ))}
                {kpis.buckets.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: "center",
                                                    padding: "0.9rem", color: "#64748B" }}>
                    No data yet for this dimension.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="hw-ci__kpi" data-testid={testid}>
      <div className="hw-ci__kpi-label">{label}</div>
      <div className="hw-ci__kpi-value">
        {typeof value === "number" ? new Intl.NumberFormat().format(value) : value}
      </div>
    </div>
  );
}
